import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import "../../CSS/Actualite.css";
import ArticleComponent from "../composant_reutilisable/ArticleComponent";
import MenuSection1 from "./menuSection1";
import { useLanguageData } from '../../hooks/useLanguageData';

const Articles = () => {
  const { t, i18n } = useTranslation();
  const [allArticles, setAllArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("TOUS LES ARTICLES");
  const [availableCategories, setAvailableCategories] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_items: 0,
    items_per_page: 10,
    total_pages: 1,
  });

  const [page, setPage] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const navigate = useNavigate();

  // Hook pour les catégories
  const { 
    data: categoriesResponse, 
    loading: categoriesLoading 
  } = useLanguageData('/api/v1/article-categories', { translate: false });

  // Hook pour charger TOUS les articles (pas de pagination côté serveur)
  const { 
    data: allArticlesResponse, 
    loading: allArticlesLoading 
  } = useLanguageData('/api/v1/embassy/articles?limit=1000', { translate: false });

  // Traiter les catégories
  useEffect(() => {
    console.log("📂 Catégories reçues de l'API:", categoriesResponse);
    
    if (categoriesResponse?.data && categoriesResponse.data.length > 0) {
      const categories = categoriesResponse.data.map(cat => ({
        id: cat.id,
        name: cat.name,
        displayName: cat.name.toUpperCase(),
        slug: cat.slug,
        description: cat.description
      }));

      console.log("✅ Catégories formatées:", categories);
      setAvailableCategories(categories);
    } else {
      console.warn("⚠️ Aucune catégorie reçue de l'API");
    }
  }, [categoriesResponse]);

  // Traiter tous les articles et faire le filtrage + pagination côté client
  useEffect(() => {
    if (allArticlesResponse?.data) {
      console.log("📚 Total articles reçus:", allArticlesResponse.data.length);
      console.log("🎯 Catégorie sélectionnée:", selectedCategory);
      
      let filtered = allArticlesResponse.data;

      // Filtrer par catégorie
      if (selectedCategory !== "TOUS LES ARTICLES" && selectedCategory !== t('articles.allArticles')) {
        // Trouver le nom de la catégorie à filtrer
        let categoryToFilter = selectedCategory;
        
        // Si la catégorie sélectionnée est en majuscules (displayName), on cherche le nom original
        const foundCategory = availableCategories.find(cat => 
          cat.displayName === selectedCategory || cat.name === selectedCategory
        );
        
        if (foundCategory) {
          categoryToFilter = foundCategory.name;
        }
        
        console.log(`🔍 Filtrage des articles avec la catégorie: "${categoryToFilter}"`);
        
        filtered = filtered.filter(article => {
          const articleCategory = article.articleCategory || '';
          return articleCategory === categoryToFilter;
        });
        
        console.log(`✅ ${filtered.length} articles trouvés dans la catégorie "${categoryToFilter}"`);
      } else {
        console.log(`📋 Aucun filtre - tous les articles (${filtered.length})`);
      }

      // Sauvegarder tous les articles filtrés
      setAllArticles(filtered);
      setTotalArticles(filtered.length);

      // Pagination côté client
      const itemsPerPage = 10;
      const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
      const start = (page - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      const paginated = filtered.slice(start, end);

      setPagination({
        current_page: page,
        total_items: filtered.length,
        items_per_page: itemsPerPage,
        total_pages: totalPages,
      });

      // Formater les articles paginés
      const formattedArticles = paginated.map((a) => ({
        id: a.id,
        slug: a.slug,
        link: a.url || `/article/${a.slug}`,
        date: `${a.articleCategory || ''} | ${new Date(a.date?.date || a.created_at).toLocaleDateString(
          "fr-FR",
          { day: "2-digit", month: "long", year: "numeric" }
        )}`,
        title: a.title,
        image: a.banner
          ? a.banner.replace(/([^:]\/)\/+/g, "$1")
          : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR8bikI-KUuM1IWosgqDRS5jyv2U_PPYlG6Tg&s",
        alt: a.slug,
        category: a.articleCategory,
        fullArticle: a
      }));

      setFilteredArticles(formattedArticles);
    }
  }, [allArticlesResponse, page, selectedCategory, t, availableCategories]);

  // Gérer le clic sur une catégorie
  const handleCategoryClick = (category) => {
    console.log(`🎯 Catégorie sélectionnée: "${category}"`);
    
    let internalCategory = category;
    // Si la catégorie est "TOUS LES ARTICLES" (traduit), la convertir
    if (category === t('articles.allArticles')) {
      internalCategory = "TOUS LES ARTICLES";
    }
    
    setSelectedCategory(internalCategory);
    setPage(1); // Revenir à la page 1 quand on change de catégorie
  };

  const handleArticleClick = (article) => {
    navigate(`/article/${article.id}/${article.slug}`, {
      state: {
        articleData: article.fullArticle,
        recentArticles: allArticles.slice(0, 5)
      }
    });
  };

  // Gérer le changement de page
  const handlePageChange = (newPage) => {
    console.log(`📄 Navigation vers la page: ${newPage}`);
    if (newPage !== page) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (num) => handlePageChange(num);
  const prevPage = () => page > 1 && handlePageChange(page - 1);
  const nextPage = () => page < pagination.total_pages && handlePageChange(page + 1);

  // Composant MenuSection personnalisé
  const CustomMenuSection = () => {
    // Récupérer les noms des catégories depuis l'API
    const apiCategories = availableCategories.map(cat => cat.displayName);
    
    // Construire la liste des catégories à afficher
    const displayCategories = [
      t('articles.allArticles'), // "TOUS LES ARTICLES" ou "ALL ARTICLES"
      ...apiCategories
    ];

    // La catégorie sélectionnée à afficher
    const displaySelected = selectedCategory === "TOUS LES ARTICLES"
      ? t('articles.allArticles')
      : selectedCategory;

    console.log("📋 Catégories à afficher dans le menu:", displayCategories);
    console.log("🎯 Catégorie sélectionnée:", displaySelected);
    console.log("📦 Catégories disponibles (brutes):", availableCategories);

    return (
      <MenuSection1
        categories={displayCategories}
        selectedCategory={displaySelected}
        onCategoryClick={handleCategoryClick}
        loading={categoriesLoading}
      />
    );
  };

  // Afficher les numéros de page
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <li key={1} className="list">
          <a className="vt-5-in hz-20-in border pointer" onClick={() => goToPage(1)}>
            1
          </a>
        </li>
      );
      if (startPage > 2) {
        pages.push(<li key="ellipsis1" className="list">...</li>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`list ${i === page ? "warning" : ""}`}>
          <a
            className={`vt-5-in hz-20-in border ${i === page ? "pointer-events-none" : "pointer"}`}
            onClick={() => goToPage(i)}
          >
            {i}
          </a>
        </li>
      );
    }

    if (endPage < pagination.total_pages) {
      if (endPage < pagination.total_pages - 1) {
        pages.push(<li key="ellipsis2" className="list">...</li>);
      }
      pages.push(
        <li key={pagination.total_pages} className="list">
          <a
            className="vt-5-in hz-20-in border pointer"
            onClick={() => goToPage(pagination.total_pages)}
          >
            {pagination.total_pages}
          </a>
        </li>
      );
    }

    return pages;
  };

  const LoadingSpinner = () => (
    <div style={{ textAlign: "center", padding: "3rem" }}>
      <div style={{
        width: "40px",
        height: "40px",
        border: "4px solid #f3f3f3",
        borderTop: "4px solid #1a3c5e",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        margin: "0 auto 1rem"
      }}></div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  const loading = allArticlesLoading || categoriesLoading;

  return (
    <div className="actualite-container">
      <div className="actualite-content">
        <CustomMenuSection />

        {loading ? (
          <LoadingSpinner />
        ) : filteredArticles.length === 0 ? (
          <p style={{ textAlign: "center" }}>
            {selectedCategory !== "TOUS LES ARTICLES" 
              ? `Aucun article dans la catégorie "${selectedCategory}"`
              : t('articles.noArticles')
            }
            {totalArticles > 0 && (
              <span style={{ display: 'block', marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                Total: {totalArticles} {t('articles.articlesLoaded')}
              </span>
            )}
          </p>
        ) : (
          <>
            <ArticleComponent
              articles={filteredArticles}
              onArticleClick={handleArticleClick}
            />

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination mb-4">
                {page > 1 && (
                  <li className="list">
                    <a
                      className="vt-5-in hz-5-in border pointer"
                      onClick={prevPage}
                    >
                      ← {t('pagination.previous')}
                    </a>
                  </li>
                )}

                {renderPageNumbers()}

                {page < pagination.total_pages && (
                  <li className="list">
                    <a
                      className="vt-5-in hz-5-in border pointer"
                      onClick={nextPage}
                    >
                      {t('pagination.next')} →
                    </a>
                  </li>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Articles;